import { forwardRef } from 'react';
import styled from '@emotion/styled';
import * as tokens from '@tokens';
import { useToggleGroup } from './toggle-group-context';
import type { IconButtonProps } from '@components/IconButton/IconButton';
import { IconButton } from '@components/IconButton/IconButton';

export interface IconToggleProps
  extends Pick<
    IconButtonProps<'button'>,
    'description' | 'icon' | 'onClick' | 'tooltipText'
  > {
  /**
   * Value of the toggle.
   */
  value: string;
  /**
   * Styles override.
   */
  className?: string;
  /**
   * Indicates if toggle is rendered as disabled.
   */
  isDisabled?: boolean;
}

const StyledIconButton = styled(IconButton)`
  button {
    width: ${tokens.spacer.x4};
    height: ${tokens.spacer.x4};

    &:hover {
      background-color: ${tokens.BRAND_COLORS.neutral_300};
    }

    &[aria-checked='true'] {
      background: ${tokens.color.ui_background};
      ${tokens.elevation(1)};
    }

    &:focus {
      ${tokens.focus()};
    }

    &[disabled] {
      background-color: ${tokens.color.ui_disabled};
    }
  }
`;

/**
 * Toggle component.
 *
 * {@link https://zeroheight.com/76c439891/p/57c441-button-toggle-group Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-togglegroup Code documentation}
 *
 * @example
 * <ToggleGroup.IconToggle value="option A" icon="..." isSelected onClick={} tooltipText="Action description" />
 */
export const IconToggle = forwardRef<HTMLButtonElement, IconToggleProps>(
  ({ icon, isDisabled, ...props }: IconToggleProps, ref) => {
    const { orientation, selectedValue } = useToggleGroup();
    const tooltipPlacement = orientation === 'vertical' ? 'right' : 'top';

    const isSelected = Boolean(selectedValue) && selectedValue === props.value;

    return (
      <StyledIconButton
        {...props}
        isDisabled={isDisabled}
        aria-checked={isSelected}
        icon={icon}
        kind="ghost"
        ref={ref}
        role="radio"
        tooltipPlacement={tooltipPlacement}
      />
    );
  },
);

IconToggle.displayName = 'IconToggle';

IconToggle.defaultProps = {
  isDisabled: false,
};
